import { videoEditor } from '@mi-front/api-clients';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFragment } from '../../hooks/useFragment';

interface CreateVideoEditionProjectDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Dialog to create a new video editor project linked to the current fragment.
 * Prompts for project name and calls the create API.
 */
export function CreateVideoEditionProjectDialog({
  open,
  onClose,
}: CreateVideoEditionProjectDialogProps) {
  const { fragment } = useFragment();
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const createMutation = videoEditor.useProjectsCreate({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: videoEditor.projectsListQueryKey({
            sourceType: 'FRAGMENT',
            sourceId: fragment.id,
          }),
        });
        onClose();
      },
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.elements.namedItem('name') as HTMLInputElement | null;
    const name = input?.value?.trim();
    if (!name) return;
    createMutation.mutate({
      data: {
        sourceType: videoEditor.createProjectDtoSourceTypeEnum.FRAGMENT,
        sourceId: fragment.id,
        name,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="fragments.videoEditions.createDialog.title"
          defaultMessage="Crear proyecto de edición"
        />
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            name="name"
            label={$t({
              id: 'fragments.videoEditions.createDialog.nameLabel',
              defaultMessage: 'Nombre',
            })}
            placeholder={$t({
              id: 'fragments.videoEditions.createDialog.namePlaceholder',
              defaultMessage: 'Nombre del proyecto',
            })}
            fullWidth
            required
            autoFocus
            error={!!createMutation.error}
            helperText={createMutation.error ? (createMutation.error as Error).message : undefined}
            disabled={createMutation.isPending}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={createMutation.isPending}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
          </Button>
          <Button type="submit" variant="contained" disabled={createMutation.isPending}>
            <FormattedMessage id="common.actions.createEntity" defaultMessage="Crear" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
